import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const RipenessGuide = () => {
  const { t } = useTranslation();

  const ripenessStages = [
    {
      key: 'veryFirm',
      image: '/avocado-01.webp',
      dotColor: 'bg-red-500',
      borderColor: 'border-red-500',
    },
    {
      key: 'firm',
      image: '/avocado-02.webp',
      dotColor: 'bg-orange-500',
      borderColor: 'border-orange-500',
    },
    {
      key: 'almostRipe',
      image: '/avocado-03.webp',
      dotColor: 'bg-yellow-500',
      borderColor: 'border-yellow-500',
    },
    {
      key: 'soft',
      image: '/avocado-04.webp',
      dotColor: 'bg-green-500',
      borderColor: 'border-green-500',
    },
    {
      key: 'softAndRipe',
      image: '/avocado-05.webp',
      dotColor: 'bg-emerald-500',
      borderColor: 'border-emerald-500',
    },
  ];

  return (
    <section className='py-16 lg:py-24 bg-gray-50'>
      <div className='container mx-auto px-4 sm:px-6 lg:px-8'>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className='text-center mb-16 lg:mb-20'
        >
          <h2 className='text-4xl sm:text-5xl lg:text-6xl font-eras font-bold text-avoprime-primary mb-6 leading-tight'>
            {t('ripenessGuide.subtitle')}
          </h2>
          <p className='text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8'>
            {t('ripenessGuide.description')}
          </p>
        </motion.div>

        {/* Mobile Layout */}
        <div className='lg:hidden'>
          <div className='space-y-6'>
            {ripenessStages.map((stage, index) => (
              <motion.div
                key={stage.key}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`rounded-2xl p-6 border ${stage.borderColor} shadow-sm bg-transparent`}
              >
                <div className='flex items-center space-x-4'>
                  {/* Left side - Dot and Image */}
                  <div className='flex items-center space-x-3 flex-shrink-0'>
                    {/* Dot indicator */}
                    <div className={`w-6 h-6 ${stage.dotColor} rounded-full`} />
                    
                    {/* Image */}
                    <div className='w-20 h-20 p-2'>
                      <img
                        src={stage.image}
                        alt={t(`ripenessGuide.stages.${stage.key}.title`)}
                        className='w-full h-full object-contain'
                      />
                    </div>
                  </div>
                  
                  {/* Content - Centered */}
                  <div className='flex-1 min-w-0 text-center'>
                    <h3 className='text-lg font-eras font-bold text-gray-800 mb-1 h-6 flex items-center justify-center'>
                      {t(`ripenessGuide.stages.${stage.key}.title`)}
                    </h3>
                    <p className='text-sm text-gray-600 leading-relaxed h-12 flex items-center justify-center'>
                      {t(`ripenessGuide.stages.${stage.key}.description`)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className='hidden lg:block'>
          <div className='grid grid-cols-5 gap-6'>
            {ripenessStages.map((stage, index) => (
              <motion.div
                key={stage.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className='text-center group'
              >
                <div className={`rounded-2xl p-6 border ${stage.borderColor} shadow-sm hover:shadow-md transition-all duration-300 h-full flex flex-col bg-transparent`}>
                  {/* Dot indicator */}
                  <div className='mb-4'>
                    <div className={`w-6 h-6 ${stage.dotColor} rounded-full mx-auto`} />
                  </div>
                  
                  {/* Image */}
                  <div className='mb-6 flex-1 flex items-center justify-center'>
                    <div className='w-32 h-32 p-4'>
                      <img
                        src={stage.image}
                        alt={t(`ripenessGuide.stages.${stage.key}.title`)}
                        className='w-full h-full object-contain transition-transform duration-300 group-hover:scale-105'
                      />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className='space-y-3'>
                    <h3 className='text-lg font-eras font-bold text-gray-800 h-6 flex items-center justify-center'>
                      {t(`ripenessGuide.stages.${stage.key}.title`)}
                    </h3>
                    <p className='text-sm text-gray-600 leading-relaxed h-12 flex items-start justify-center'>
                      {t(`ripenessGuide.stages.${stage.key}.description`)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Tip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          viewport={{ once: true }}
          className='text-center mt-16'
        >
          <div className='inline-flex items-center space-x-3 bg-white rounded-full px-4 sm:px-6 py-3 shadow-sm border border-gray-200 max-w-sm sm:max-w-none'>
            <div className='w-2 h-2 bg-avoprime-primary rounded-full animate-pulse flex-shrink-0' />
            <span className='text-[10px] sm:text-sm font-montserrat text-gray-600 leading-relaxed'>
              {t('ripenessGuide.tip')}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default RipenessGuide;
